import PropTypes from 'prop-types'

import {useEffect, useMemo} from 'react'
import {connect} from 'react-redux'
import {List, Map} from 'immutable'
import {updateResource, createResource} from 'redux-json-api'

import {Grid} from '@mui/material'

import NextBestActions from '../../../../../shared_components/next_best_actions/NextBestActions'
import ContactCard from '../../contact_card/ContactCard'
import SharingModuleController from '../../sharing_module/SharingModuleController'
import DashboardCategoryHeader from './DashboardCategoryHeader'
import EverplansArticles from '../../../../../shared_components/everplans_articles/EverplansArticles'
import FileStatus from './FileStatus'
import ItemsWithoutResponses from './ItemsWithoutResponses'
import ItemsWithResponses from './ItemsWithResponses'
import LifeStoryHero from '../life_story/LifeStoryHero'
import LifeStoryUndoBar from '../life_story/LifeStoryUndoBar'
import {usePendingLifeStoryDeletion} from '../life_story/lifeStoryPendingDeletion'
import {
  LIFE_AND_LEGACY_CATEGORY_ID,
  withoutPendingLifeStoryGroup
} from '../life_story/lifeStoryTools'

import {categoryNameFromSlug} from '../../../../../lib/responsesHelper'
import {cleanResponse} from '../redux/apiHelper'
import {hasMyStoryAccess, isFuneralHome} from '../../../../../pro/src/lib/firmTools'
import {currentUserConfig, userAssessment, userPartner, userProfile} from '../../../../../lib/userTools'
import {EVERPLANS_ARTICLES_BY_CATEGORY} from './everplansArticlesByCategory'
import {filterNextBestActionsByItems} from '../../../lib/nextBestActionTools'
import routerUtils from '../../../../../lib/routerUtils'
import {scrollToTopOfPage} from '../../../../../lib/scrollTools'
import {usePrevious, useEverplanData} from '../../../../../lib/hooks'

import {
  filterRawResourceByAttributeId,
  findFirmConfigByFirmOwnership,
  findRawResourceByAttributeId,
  firmForEverplan,
  isOwnEverplan
} from '../../../../../lib/plan_data/userData'

import './category.scss'

export const Category = props => {
  const {category, children, isFuneralHomeClient, params, userConfig} = props
  const myStoryEnabled = props.hasMyStoryAccess

  const context = 'category_page'

  const {itemResponses: allItemResponses, newContacts} = useEverplanData(userConfig.get('everplan-id'))
  // A life story inside its undo-after-delete window is already gone as far
  // as the user is concerned: its vault group stays hidden until the deferred
  // DELETE lands and the item responses refresh. The whole page (next best
  // actions, sharing module, item lists) works from the filtered list.
  const pendingLifeStoryDeletion = usePendingLifeStoryDeletion()
  const itemResponses = withoutPendingLifeStoryGroup({
    itemResponses: allItemResponses,
    pendingDeletion: pendingLifeStoryDeletion
  })
  const categoryItemResponses = itemResponses.filter(itemResponse => itemResponse.get('category-id') === category.get('id'))
  const isClient = userConfig.get('client')
  const nextBestActions = useMemo(() => filterNextBestActionsByItems({...props, itemResponses: categoryItemResponses}), [categoryItemResponses])

  const prevParamSlug = usePrevious(params.slug)
  const isLifeAndLegacyCategory = category?.get('id') === LIFE_AND_LEGACY_CATEGORY_ID
  const showPrearrangedFuneralCard = isLifeAndLegacyCategory
  const categoryArticles = EVERPLANS_ARTICLES_BY_CATEGORY.get(category.get('name')) || List()

  const FUNERAL_HOME_CLIENT_HIDDEN_ARTICLE_QUERIES = [
    'how-to-pre-plan-your-funeral',
    'all-you-need-to-know-about-cremation'
  ]

  const filteredCategoryArticles = useMemo(() => {
    if (!isFuneralHomeClient || category?.get('id') !== LIFE_AND_LEGACY_CATEGORY_ID) return categoryArticles

    return categoryArticles.filter(article => !FUNERAL_HOME_CLIENT_HIDDEN_ARTICLE_QUERIES.includes(article?.get('query')))
  }, [categoryArticles, category, isFuneralHomeClient])

  useEffect(() => {
    scrollToTopOfPage()
  }, [])

  useEffect(() => {
    if (prevParamSlug !== params.slug)
      scrollToTopOfPage()
  }, [params.slug])

  const nextBestActionsElement = (
    <NextBestActions {...props} nextBestActions={nextBestActions} itemResponses={itemResponses} newContacts={newContacts} context='category_page' />
  )

  return (
    <article className='category flex-container'>
      <FileStatus />
      <LifeStoryUndoBar />
      <div className='category-container'>
        <div className='category-inner'>
          <Grid container columnSpacing={{mobileSm: 0, tabletLg: 6}}>
            <Grid item mobileSm={12} tabletLg={8.4}>
              <DashboardCategoryHeader category={category} userConfig={userConfig} />
              {
                isLifeAndLegacyCategory && myStoryEnabled ?
                  <LifeStoryHero
                    categorySlug={params.slug}
                    fallback={nextBestActionsElement}
                    location={props.location}
                    ownEverplan={props.ownEverplan}
                    userConfig={userConfig}
                  /> :
                  nextBestActionsElement
              }
              <ItemsWithResponses
                {...props}
                itemResponses={itemResponses}
                newContacts={newContacts}
                categoryItemResponses={categoryItemResponses}
                actor='planholder'
                category={category}
                categorySlug={params.slug}
                context={context}
                showPrearrangedFuneralCard={showPrearrangedFuneralCard}
              />
              <ItemsWithoutResponses
                categoryItemResponses={categoryItemResponses}
                category={category}
                hasMyStoryAccess={myStoryEnabled}
                isFuneralHomeClient={isFuneralHomeClient}
                pathname={props.location.pathname}
                categorySlug={params.slug}
                context={context}
                userConfig={userConfig}
              />
              <EverplansArticles
                userConfig={userConfig}
                articles={filteredCategoryArticles}
                context={context}
                location={props.location}
              />
            </Grid>
            <Grid className='right-column' item mobileSm={12} tabletLg={3.6}>
              {isClient && <ContactCard />}
              <SharingModuleController
                {...props}
                context={context}
                itemResponses={itemResponses}
                newContacts={newContacts}
              />
            </Grid>
          </Grid>
          {
            routerUtils.childrenWithProps(
              children,
              {
                owners: props.owners,
                firmConfig: props.firmConfig,
                ownEverplan: props.ownEverplan,
                context: 'category_page',
                profile: props.profile,
                userConfig,
                nextBestActions
              }
            )
          }
        </div>
      </div>
    </article>
  )
}

Category.defaultProps = {
  params: {slug: ''}
}

Category.propTypes = {
  assessment: PropTypes.instanceOf(Map),
  categories: PropTypes.instanceOf(List),
  category: PropTypes.instanceOf(Map),
  categoryItems: PropTypes.instanceOf(List),
  firmConfig: PropTypes.instanceOf(Map),
  hasMyStoryAccess: PropTypes.bool,
  isFuneralHomeClient: PropTypes.bool,
  items: PropTypes.instanceOf(List),
  kits: PropTypes.instanceOf(Map),
  location: PropTypes.object,
  nextBestActions: PropTypes.instanceOf(List),
  owners: PropTypes.instanceOf(List),
  ownEverplan: PropTypes.bool,
  profile: PropTypes.instanceOf(Map),
  params: PropTypes.shape({
    slug: PropTypes.string
  }),
  updateResource: PropTypes.func,
  userConfig: PropTypes.instanceOf(Map)
}

export const mapStateToProps = (state, {params}) => {
  const userConfig = currentUserConfig(state.api)
  const profile = userProfile({profiles: state.api.profiles, userConfig})
  const everplanId = userConfig.get('everplan-id')

  const firm = firmForEverplan({firmOwnerships: state.api['firm-ownerships'], firms: state.api.firms, userConfig})

  const vaultExtension = findRawResourceByAttributeId({
    rawResource: state.api['vault-extensions'],
    attribute: 'everplan-id',
    id: everplanId
  })

  // Using the `slug` to get a 'rough draft' of the actual name, then using the actual `category` once we have that
  // available to avoid subtle capitalization changes (e.g., `Family And Loved Ones` versus the correct version
  // `Family and Loved Ones`). --BLR
  const rawCategoryName = categoryNameFromSlug(params.slug).toLowerCase()
  const category = state.categories.find(
    candidateCategory => (candidateCategory.get('name').toLowerCase() === rawCategoryName)
  )
  const assessment = userAssessment({userConfig, assessments: state.api.assessments})

  const gapFillerContentDownloads = filterRawResourceByAttributeId({
    attribute: 'state',
    id: profile.get('state'),
    rawResource: state.api['content-downloads']
  })

  return ({
    assessment,
    isFuneralHomeClient: isFuneralHome(firm),
    hasMyStoryAccess: hasMyStoryAccess(firm),
    partner: userPartner({userConfig, partners: state.api.partners}),
    nextBestActions: cleanResponse(state.api['next-best-actions']),
    category,
    ownEverplan: isOwnEverplan({everplanPreviewId: everplanId, owners: state.owners}),
    categories: state.categories,
    gapFillerContentDownloads,
    owners: state.owners,
    profile,
    userConfig,
    itemViews: filterRawResourceByAttributeId({
      attribute: 'everplan-id',
      id: userConfig.get('everplan-id'),
      rawResource: state.api['item-views']
    }),
    firmConfig: findFirmConfigByFirmOwnership({firmOwnerships: state.api['firm-ownerships'], firmConfigs: state.api['firm-configs'], userConfig}),
    vaultExtension
  })
}

export default connect(mapStateToProps, {updateResource, createResource})(Category)
